import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '../stores/auth.js'

// Layout
import AppLayout from '../components/layout/AppLayout.vue'

// Auth Views
import LoginPage from '../views/auth/LoginPage.vue'
import VerifyOtpPage from '../views/auth/VerifyOtpPage.vue'
import MagicLinkPage from '../views/auth/MagicLinkPage.vue'

// Authenticated Views
import DatasetList from '../views/dataset/DatasetList.vue'
import DatasetDetail from '../views/dataset/DatasetDetail.vue'
import RevenueReport from '../views/dataset/RevenueReport.vue'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // Auth routes (no layout)
    {
      path: '/auth/login',
      name: 'login',
      component: LoginPage,
      meta: { title: 'ログイン', guest: true }
    },
    {
      path: '/auth/verify-otp',
      name: 'verify-otp',
      component: VerifyOtpPage,
      meta: { title: 'OTP認証', guest: true }
    },
    {
      path: '/auth/magic/:token',
      name: 'magic-link',
      component: MagicLinkPage,
      meta: { title: 'マジックリンク認証', guest: true }
    },

    // Public documentation (no auth required)
    {
      path: '/docs/api',
      name: 'api-docs',
      component: () => import('../views/ApiDocumentation.vue'),
      meta: { title: 'API ドキュメント' }
    },

    // Public legal pages (no auth required)
    {
      path: '/commerce',
      name: 'commerce',
      component: () => import('../views/CommercePage.vue'),
      meta: { title: '特定商取引法に基づく表記' }
    },

    // Authenticated routes (with AppLayout)
    {
      path: '/dashboard',
      name: 'dashboard',
      component: () => import('../views/Dashboard.vue'),
      meta: { requiresAuth: true, title: 'ダッシュボード' }
    },
    {
      path: '/api-keys',
      name: 'api-keys',
      component: () => import('../views/ApiKeysPage.vue'),
      meta: { requiresAuth: true, title: 'APIキー管理' }
    },
    {
      path: '/datasets',
      name: 'datasets-management',
      component: () => import('../views/DatasetsPage.vue'),
      meta: { requiresAuth: true, title: 'POIデータセット管理' }
    },
    {
      path: '/poi',
      name: 'poi',
      component: () => import('../views/PoiPage.vue'),
      meta: { requiresAuth: true, title: 'POI管理' }
    },
    {
      path: '/marketplace',
      name: 'marketplace',
      component: DatasetList,
      meta: { requiresAuth: true, title: '公開データセット' }
    },
    {
      path: '/marketplace/:datasetId',
      name: 'marketplace-detail',
      component: DatasetDetail,
      meta: { requiresAuth: true, title: 'データセット詳細' }
    },
    {
      path: '/usage',
      name: 'usage',
      component: () => import('../views/UsagePage.vue'),
      meta: { requiresAuth: true, title: '使用量' }
    },
    {
      path: '/billing',
      name: 'billing',
      component: () => import('../views/BillingPage.vue'),
      meta: { requiresAuth: true, title: '請求・支払い' }
    },
    {
      path: '/profile',
      name: 'profile',
      component: () => import('../views/ProfilePage.vue'),
      meta: { requiresAuth: true, title: 'プロフィール' }
    },
    {
      path: '/upgrade',
      name: 'upgrade',
      component: () => import('../views/UpgradeAccount.vue'),
      meta: { requiresAuth: true, title: 'アカウントアップグレード' }
    },
    {
      path: '/payment-method',
      name: 'payment-method',
      component: () => import('../views/PaymentMethodPage.vue'),
      meta: { requiresAuth: true, title: '支払い方法' }
    },
    {
      path: '/payment-method/complete',
      name: 'payment-complete',
      component: () => import('../views/PaymentCompletePage.vue'),
      meta: { requiresAuth: true, title: 'カード登録完了' }
    },
    {
      path: '/revenue',
      name: 'revenue',
      component: RevenueReport,
      meta: { requiresAuth: true, requiresDataProvider: true, title: '収益レポート' }
    },

    // Admin routes (TASK-FE-301)
    {
      path: '/admin/dashboard',
      name: 'admin-dashboard',
      component: () => import('../views/admin/AdminDashboard.vue'),
      meta: { requiresAuth: true, requiresAdmin: true, title: '管理者ダッシュボード' }
    },
    {
      path: '/admin/users',
      name: 'admin-users',
      component: () => import('../views/admin/UserManagement.vue'),
      meta: { requiresAuth: true, requiresAdmin: true, title: 'ユーザー管理' }
    },
    {
      path: '/admin/corporate',
      name: 'admin-corporate',
      component: () => import('../views/admin/CorporateManagement.vue'),
      meta: { requiresAuth: true, requiresAdmin: true, title: '法人アカウント管理' }
    },
    {
      path: '/admin/transfers',
      name: 'admin-transfers',
      component: () => import('../views/admin/TransferConfirmation.vue'),
      meta: { requiresAuth: true, requiresAdmin: true, title: '振込確認' }
    },
    {
      path: '/admin/pricing',
      name: 'admin-pricing',
      component: () => import('../views/admin/PricingManagement.vue'),
      meta: { requiresAuth: true, requiresAdmin: true, title: '価格設定' }
    },
    {
      path: '/admin/revenue',
      name: 'admin-revenue',
      component: () => import('../views/admin/RevenueAnalytics.vue'),
      meta: { requiresAuth: true, requiresAdmin: true, title: '収益集計' }
    },

    // Catch-all redirect
    {
      path: '/:pathMatch(.*)*',
      redirect: '/dashboard'
    }
  ]
})

// Navigation guard for authentication
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // Initialize auth store if not initialized
  if (!authStore.initialized) {
    await authStore.initialize()
  }

  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)
  const isGuest = to.matched.some(record => record.meta.guest)

  // Redirect authenticated users away from guest pages (login, register)
  if (isGuest && authStore.isAuthenticated) {
    next('/dashboard')
    return
  }

  // Redirect unauthenticated users to login
  if (requiresAuth && !authStore.isAuthenticated) {
    next({
      path: '/auth/login',
      query: { redirect: to.fullPath }
    })
    return
  }

  // Check admin permission for admin routes (TASK-FE-301)
  const requiresAdmin = to.matched.some(record => record.meta.requiresAdmin)
  if (requiresAdmin && !authStore.isAdmin) {
    next('/dashboard')
    return
  }

  // Check data provider permission for revenue routes
  const requiresDataProvider = to.matched.some(record => record.meta.requiresDataProvider)
  if (requiresDataProvider && !authStore.canAccessRevenue) {
    next('/dashboard')
    return
  }

  next()
})

export default router
